import { LOIS_ORBIT_ROT, LOIS_ORBIT_RX, LOIS_ORBIT_RY, LOIS_SPARKLE_SM } from './losi'
import { TAU, clamp, createRng, r2 } from './math'

/* ------------------------------------------------------------------ colours */

/**
 * Rings are not flat colours: the video shows a full hue wheel at constant
 * luminance, with a gradient along each stroke. Measured: S 45-62%, L 50-67%.
 */
function wheel(hue: number, s = 0.55, l = 0.62): string {
  const h = ((hue % 360) + 360) % 360
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  const [r, g, b] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x]
  const hex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${hex(r)}${hex(g)}${hex(b)}`
}

/* ------------------------------------------------------------- render types */

export interface DotRender {
  x: number
  y: number
  r: number
  opacity: number
  /** explicit colour; by default rendering uses the body colour */
  color?: string
  /**
   * Depth fog: 0 = faded into the background, 1 = full body colour.
   * Blending happens at render time, which alone knows the chosen colour.
   */
  depth?: number
  /**
   * Non-circular shape, in ball-radius units and centred on the origin
   * (the tilted "!" dot is a droplet, not a disc). When provided, `r` is no
   * longer used for drawing.
   */
  d?: string
  /** rotation applied to `d`, in degrees */
  rot?: number
  /** Path scale in ball-radius units (`LOIS_SPARKLE_SM` default ≈0.075). */
  pathScale?: number
  /**
   * Decor sitting outside the silhouette (agent UI, magnifier, chat bubble).
   * Renders in a contrasting colour so it stays visible on any body/palette pick.
   */
  accent?: boolean
}

/**
 * What a state declares: arc geometry stays in ball-radius units; the engine
 * (the only one that knows viewBox scale) rasterises it. Without that, states
 * would need to know the viewBox.
 */
export interface ArcSpec {
  id: string
  seed: ArcSeed
  t: number
  opacity: number
}

export interface ArcRender {
  id: string
  /** portion in front of the body */
  front: string
  /** portion behind the body (drawn first, therefore masked by the silhouette) */
  back: string
  width: number
  opacity: number
  /** hue gradient along the stroke */
  grad: { x1: number; y1: number; x2: number; y2: number; stops: string[] }
}

/* --------------------------------------------------------- 3D elliptic arc */

export interface ArcSeed {
  /** semi-major axis, in ball-radius units */
  a: number
  /** flattening b/a: measured <= 0.45, orbit planes seen edge-on */
  k: number
  /** major-axis tilt on screen, radians */
  tilt: number
  /** turns per second */
  speed: number
  phase: number
  /** fraction of the full turn actually drawn */
  sweep: number
  hue: number
  hueSpan: number
  width: number
  cx: number
  cy: number
  /** When set, replaces the hue-wheel gradient (Losi orbit trails). */
  stops?: string[]
}

/**
 * Projects an inclined 3D circle orthographically.
 *
 * The circle lives in the plane spanned by u (in screen) and v (into depth). The
 * z component splits the arc in two: the back half is drawn before the body, so
 * occluded by it. That real depth sort is what makes rings read as orbits, not
 * flat drawing.
 */
export function arcRender(seed: ArcSeed, t: number, scale: number, id: string, opacity = 1): ArcRender {
  const spin = seed.phase + t * seed.speed * TAU
  const cu = Math.cos(seed.tilt)
  const su = Math.sin(seed.tilt)
  const kz = Math.sqrt(Math.max(0, 1 - seed.k * seed.k))

  const N = 64
  const span = seed.sweep * TAU
  let front = ''
  let back = ''
  let prev: boolean | null = null

  for (let i = 0; i <= N; i++) {
    const th = spin + (i / N) * span
    const ct = Math.cos(th)
    const st = Math.sin(th)
    // u = (cos tilt, sin tilt, 0) ; v = (-sin tilt * k, cos tilt * k, kz)
    const x = seed.a * (ct * cu + st * -su * seed.k) + seed.cx
    const y = seed.a * (ct * su + st * cu * seed.k) + seed.cy
    const z = seed.a * st * kz

    const behind = z < 0
    const sx = r2(x * scale)
    const sy = r2(y * scale)
    const cmd = behind !== prev ? 'M' : 'L'
    if (behind) back += `${cmd}${sx} ${sy}`
    else front += `${cmd}${sx} ${sy}`
    prev = behind
  }

  const gx = Math.cos(seed.tilt) * seed.a * scale
  const gy = Math.sin(seed.tilt) * seed.a * scale
  return {
    id,
    front,
    back,
    width: seed.width * scale,
    opacity,
    grad: {
      x1: r2(seed.cx * scale - gx),
      y1: r2(seed.cy * scale - gy),
      x2: r2(seed.cx * scale + gx),
      y2: r2(seed.cy * scale + gy),
      stops: seed.stops ?? [
        wheel(seed.hue),
        wheel(seed.hue + seed.hueSpan * 0.5),
        wheel(seed.hue + seed.hueSpan)
      ]
    }
  }
}

/* ---------------------------------------------------------------- rings */

const RING_RNG = createRng(0xa11ce)

/**
 * 6 rings, semi-major axis 1.30-1.40 (clearly larger than the ball),
 * flattening always <= 0.45, thickness 0.055, ~3.3 turns/s.
 */
export const RINGS: ArcSeed[] = Array.from({ length: 6 }, (_, i) => ({
  a: 1.3 + RING_RNG() * 0.1,
  k: 0.05 + RING_RNG() * 0.4,
  tilt: (i / 6) * Math.PI + RING_RNG() * 0.5,
  speed: 3 + RING_RNG() * 0.7,
  phase: RING_RNG() * TAU,
  sweep: 0.6 + RING_RNG() * 0.25,
  hue: (i * 360) / 6 + RING_RNG() * 30,
  hueSpan: 60 + RING_RNG() * 60,
  width: 0.05 + RING_RNG() * 0.012,
  cx: 0,
  cy: 0.1
}))

/**
 * Nested arc bouquet sweeping the triangle just before the orbits.
 * Seen almost edge-on (hence the bobby-pin shape), rmax 1.37.
 */
export const SWOOSH: ArcSeed[] = Array.from({ length: 4 }, (_, i) => ({
  a: 0.78 + i * 0.2,
  k: 0.05 + i * 0.02,
  tilt: -0.62 + i * 0.05,
  speed: 0.3,
  phase: 0.06 * i,
  sweep: 0.4,
  hue: 95 + i * 62,
  hueSpan: 100,
  width: 0.05,
  cx: 0,
  cy: -0.12
}))

/* ------------------------------------------------------------- 3 dots */

/** x measured: -0.557 / -0.013 / +0.532, y = 0. */
export const DOT_X = [-0.557, -0.013, 0.532] as const
export const DOT_R = 0.165
export const DOT_PEAK = 1.25

/* ------------------------------------------------------------ particles */

const P_RNG = createRng(0xbeef)

/** 5 particles, one new every 0.2 s, lifetime 0.55 s. */
const PARTICLES = Array.from({ length: 5 }, (_, i) => ({
  birth: i * 0.2,
  angle: P_RNG() * TAU,
  rho: 0.58 + P_RNG() * 0.18
}))

/**
 * Particles don't travel in a straight line: they spiral toward the centre
 * (radius ×0.75 per frame, angle +100 deg/s) while growing, and pass behind
 * the core where they are swallowed.
 */
export function particles(t: number, scale: number): DotRender[] {
  const out: DotRender[] = []
  for (const p of PARTICLES) {
    const u = t - p.birth
    if (u < 0 || u > 0.62) continue
    const rho = p.rho * Math.pow(0.75, u * 10)
    const a = p.angle + (u * 100 * Math.PI) / 180
    const op = clamp(u / 0.06) * clamp((0.62 - u) / 0.08)
    out.push({
      x: Math.cos(a) * rho * scale,
      y: Math.sin(a) * rho * scale,
      r: 0,
      d: LOIS_SPARKLE_SM,
      rot: (a * 180) / Math.PI,
      depth: clamp(1 - rho / 0.8),
      opacity: op
    })
  }
  return out
}

/* ------------------------------------------------------------------ comet */

/** Losi orbit geometry in ball-radius units (viewBox 100 → RAYON 100). */
const ORBIT_A = LOIS_ORBIT_RY / 50
const ORBIT_K = LOIS_ORBIT_RX / LOIS_ORBIT_RY
const ORBIT_TILT = (LOIS_ORBIT_ROT * Math.PI) / 180

/** Cream trail gradient — matches the customiser default, not the x.ai hue wheel. */
export const LOSI_TRAIL_STOPS = ['#d4d0c8', '#f1efe9', '#ffffff'] as const

/**
 * Comet: the planet shrinks and the satellite streaks along the Losi orbit.
 * Same structure as the reference (dot centred, trail orbits it) but on the
 * logotype ellipse instead of rainbow ribbons.
 */
const COMET_RNG = createRng(0xc0e7)
export const COMET_RIBBONS: ArcSeed[] = Array.from({ length: 4 }, (_, i) => {
  const d = i - 1.5
  return {
    a: ORBIT_A * (1 + d * 0.025),
    k: ORBIT_K * (1 + d * 0.03),
    tilt: -ORBIT_TILT + d * 0.025,
    speed: 0.58,
    phase: -i * 0.11 + COMET_RNG() * 0.01,
    sweep: 0.3 + i * 0.02,
    hue: 0,
    hueSpan: 0,
    width: 0.082 - i * 0.006,
    cx: 0,
    cy: 0,
    stops: [...LOSI_TRAIL_STOPS]
  }
})

/** Comet dot radius, measured at 0.129. */
export const COMET_DOT = 0.129

/**
 * Hold the body at full sparkle through entry morph (`comet.morph` = 0.45 s).
 * Without this, collapse starts at local t = 0 while sleep → comet is still
 * blending (~0.38 → 1) and the silhouette shrinks mid-fade.
 */
export const COMET_COLLAPSE_DELAY = 0.45

/** Radians ahead of the ribbon tip along the orbit (clears the stroke, not on it). */
export const COMET_SAT_LEAD = 0.22

/** Satellite position on the comet trail — same ellipse math as `arcRender`. */
export function cometSatelliteBall(t: number, seed: ArcSeed = COMET_RIBBONS[0]!): { x: number; y: number } {
  const spin = seed.phase + t * seed.speed * TAU
  const th = spin + seed.sweep * TAU + COMET_SAT_LEAD
  const cu = Math.cos(seed.tilt)
  const su = Math.sin(seed.tilt)
  const ct = Math.cos(th)
  const st = Math.sin(th)
  return {
    x: seed.a * (ct * cu + st * -su * seed.k) + seed.cx,
    y: seed.a * (ct * su + st * cu * seed.k) + seed.cy
  }
}

/* --------------------------------------------------- notification badge */

/** Blue measured pixel-by-pixel. */
export const NOTIF_BLUE = '#2496e8'
/** Badge sits exactly on the circumference, at -42deg. */
export const NOTIF_ANGLE = -42
export const NOTIF_DIST = 1.003
/** Radius at rest; pop peaks 14% above. */
export const NOTIF_R = 0.15
export const NOTIF_POP = 1.14
/**
 * Notch is a disc concentric with the badge, subtracted from the body.
 * Margin is constant (0.054 R) and follows body scale.
 */
export const NOTIF_MARGIN = 0.054
